import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  Output,
  QueryList,
  signal,
  Signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';

import {
  getBackground,
  ImageShape,
  makeFontStyles,
  makeFontStyleStyles,
  PropertiesConfigImagePropertiesModel,
  PropertiesConfigInitialsStylePropertiesModel,
} from '@blaze-shared/elements-domain';
import { resizeRxUtils } from '@blaze-shared/shared-util';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { FontsService } from '@blaze-shared/theming-data-access';
import { getItemTypeIcon, isImageExtension } from '@blaze-shared/uploader';

import { EMPTY_ICON_MAP, type ImageEmptyThumbnail } from '../../elements/image/image.models';
import { type UITableCellStyling } from '../../elements/table/table-cell-styling';
import { FileService } from '../../services/file.service';
import { getUserInitials } from '../../utils/data/value.utils';

@UntilDestroy()
@Component({
  selector: 'blaze-image-column-value',
  templateUrl: './image-column-value.component.html',
  styleUrls: ['./image-column-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ImageColumnValueComponent implements OnChanges {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly fileService = inject(FileService);
  private readonly fontsService = inject(FontsService);

  imageAlign = input<string | undefined>();
  imageFormat = input<'image' | 'initials'>('image');
  imageProps = input<Partial<PropertiesConfigImagePropertiesModel>>();
  initialsColumnValue = input<string | undefined>();
  initialsProps = input<Partial<PropertiesConfigInitialsStylePropertiesModel>>();

  @Input() actionsAlwaysVisible = false;
  @Input() cellStyles? = {} as UITableCellStyling;
  @Input() emptyText?: string;
  @Input() emptyThumbnail?: ImageEmptyThumbnail;
  @Input() errorIconSize: ValueWithUnitOfMeasure = '32px';
  @Input() fillHorizontally = false;
  @Input() fillVertically = false;
  @Input() fitToContainer = false;
  @Input() isMultipleValue? = false;
  @Input() useExternalClickAction? = false;
  @Input() value: string | string[] | unknown;

  @Output() clickAction = new EventEmitter();

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChildren('imgTag') imgTags!: QueryList<ElementRef<HTMLImageElement>>;

  private readonly images$$ = new BehaviorSubject<string[]>([]);

  protected readonly initials: Signal<string>;
  protected readonly isLoading = signal(false);
  protected readonly loadImageFailed = signal(false);

  protected imageStyles: Signal<Record<string, string>>;
  protected initialsStyles: Signal<Record<string, string | undefined>>;
  protected shape: Signal<ImageShape>;
  protected wrapStyles: Signal<Record<string, string>>;

  sources: string[] = [];
  visibleImages$$ = new BehaviorSubject<Set<string>>(new Set());

  get canAddValue(): boolean {
    return !!(
      this.useExternalClickAction &&
      (this.isMultipleValue || (!this.isMultipleValue && !this.value))
    );
  }

  get isMenuOpen(): boolean {
    return !!this.menuTrigger?.menuOpen;
  }

  get emptyThumbnailIcon(): string {
    return this.emptyThumbnail ? EMPTY_ICON_MAP[this.emptyThumbnail] : '';
  }

  constructor() {
    resizeRxUtils
      .fromResizeObserver(this.elementRef.nativeElement)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.calcVisibleImages());

    this.initials = computed(() => {
      return getUserInitials(this.initialsColumnValue());
    });

    this.initStyles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      const values = Array.isArray(this.value) ? this.value : [this.value].filter(Boolean);
      const thumbnails = values
        .map((value) => getFileThumbnail(value))
        .map((thumb, index) => (!thumb ? values[index] : thumb));
      const newSource = thumbnails || values || [];

      this.loadImageFailed.set(false);
      this.images$$.next(values);

      this.sources = newSource;
      if (!newSource.every((url) => this.sources.includes(url))) {
        this.isLoading.set(true);
        this.loadImageFailed.set(false);
      }
    }
  }

  onImageLoaded(): void {
    this.loadImageFailed.set(false);
    this.isLoading.set(false);

    this.calcVisibleImages();
  }

  onImageError(): void {
    this.loadImageFailed.set(true);
    this.isLoading.set(false);
  }

  externalClickAction(
    event: Event,
    imageUrl?: string,
    element?: HTMLElement,
    isAddFileAction?: boolean,
  ): void {
    this.clickAction.emit({ event, imageUrl, element, isAddFileAction });
  }

  deleteFileFromList(event: Event, imageUrl?: string, element?: HTMLElement): void {
    this.clickAction.emit({ event, imageUrl, element, isRemoveFileAction: true });
  }

  downloadFile(event: Event, fileUrl: string | undefined): void {
    event.preventDefault();
    if (!fileUrl) {
      return;
    }
    this.fileService.downloadFile(fileUrl);
  }

  private calcVisibleImages(): void {
    const images = new Set<string>();

    let filled = 0;
    const maxPossibleWidth = this.elementRef.nativeElement.scrollWidth;
    const gap = 8;
    this.imgTags.toArray().forEach((img, index) => {
      const neededGapWidth = index === 0 ? 0 : gap;
      const thisElementWidth = neededGapWidth + img.nativeElement.scrollWidth;

      if (index === 0 || (thisElementWidth > 0 && thisElementWidth + filled <= maxPossibleWidth)) {
        /**
         * Src data from img element is urlEncoded.
         * Use custom data attribute "source" to get url as was saved in element's input.
         */
        const src = img.nativeElement.dataset?.source ?? img.nativeElement.src;
        images.add(src);
        filled += thisElementWidth;
      }
    });

    this.visibleImages$$.next(images);
  }

  private initStyles(): void {
    // Initials
    this.initialsStyles = computed(() => {
      const initialsStyle = this.initialsProps();

      if (!initialsStyle) {
        return {};
      }

      const styles = {
        ...makeFontStyles(initialsStyle.font),
        ...makeFontStyleStyles(initialsStyle.font?.style),
        ['font-weight']: this.fontsService.getFontWeight(
          initialsStyle.font?.fontFamily,
          initialsStyle.font?.fontWeight,
        ),
        color: initialsStyle?.textColor?.color1 || '#000',
        background: initialsStyle?.background
          ? getBackground(initialsStyle.background)
          : 'var(--b-t-header-bg)',
      };

      return styles;
    });

    // Image shape
    this.shape = computed(() => {
      return this.imageProps()?.shape ?? 'auto';
    });

    // Image
    this.imageStyles = computed(() => {
      const { outline, outlineWidth, radius, behavior } = this.imageProps() || {};
      return {
        'border-color': outline?.color1 ?? 'transparent',
        'border-width': outline?.selectorType !== 'none' ? (outlineWidth ?? '0px') : '0px',
        'border-radius': radius ?? '0px',
        'object-fit': behavior ?? 'cover',
      };
    });

    // Wrap
    this.wrapStyles = computed(() => {
      const imageAlign = this.imageAlign();
      const { padding } = this.imageProps() || {};
      const wrapStyles: Record<string, string> = { padding: padding ?? '0px' };

      if (imageAlign) {
        wrapStyles['display'] = 'flex';
        wrapStyles['justify-content'] = imageAlign;
      }
      return wrapStyles;
    });
  }
}

/** For URLs pointing to files which are not images a file format thumbnail should be presented */
function getFileThumbnail(url?: string | null): string | undefined {
  if (!url) {
    return;
  }

  try {
    const urlObject = new URL(url);

    const filename = urlObject.pathname.split('/').pop() || '';
    const nameParts = filename.split('.');

    // If no visible extension - treat as image
    if (nameParts.length <= 1) {
      return;
    }
    const extension = nameParts.pop()?.toLocaleLowerCase();

    if (!extension || isImageExtension(extension)) {
      return;
    } else {
      return getItemTypeIcon(`.${extension}`);
    }
  } catch {
    return undefined;
  }
}
